import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { FIRM } from "@/lib/site";
import { scrollToSection } from "@/hooks/useLenis";

const EASE = [0.33, 1, 0.68, 1];
const HERO_BG =
  "https://images.unsplash.com/photo-1628748970468-bb56c807f6e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxoaWdoJTIwY291cnQlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODQ1Mzk1MzV8MA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span style={{ display: "block" }}
      initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="top" ref={ref} data-testid="hero-section"
      className="relative min-h-screen bg-[#0A1128] bg-noise overflow-hidden flex items-end">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_BG} alt="High Court architecture" loading="eager"
          className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/70 to-[#0A1128]/40" />
      </motion.div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pb-20 pt-36">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="overline text-[#D4AF37] mb-8">
          Advocates &middot; Madhya Pradesh &middot; Est. 14 Years
        </motion.div>

        <h1 className="font-serif-display text-white text-[13vw] md:text-[8.5vw] leading-[0.92] tracking-tight">
          <Line delay={0.35}>Counsel of</Line>
          <Line delay={0.5}><span className="italic text-[#D4AF37]">Conviction</span> &amp; Craft.</Line>
        </h1>

        <div className="mt-10 grid md:grid-cols-2 gap-8 items-end">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: EASE }}
            className="font-body text-white/70 text-base md:text-lg max-w-md font-light leading-relaxed">
            Law Offices of Arjun Singh is a full service law firm representing and advising
            clients before the High Courts, District Courts, Family Courts and Tribunals across
            Madhya Pradesh — combining fourteen years of courtroom rigour with meticulous counsel.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1, ease: EASE }}
            className="flex flex-wrap gap-4 md:justify-end">
            <button data-testid="hero-book-btn"
              onClick={() => scrollToSection("#contact")}
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A1128] px-7 py-4 font-medium hover:bg-white transition-colors">
              Book a Consultation
              <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
            </button>
            <button data-testid="hero-practice-btn"
              onClick={() => scrollToSection("#practice")}
              className="inline-flex items-center gap-3 border border-white/30 text-white px-7 py-4 font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              View Practice Areas
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
